<template>
  <section class="h-full mx-4">
    <BaseTable
      :headers="rangesHeaders"
      :list="ranges"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="loadData"
      table-id="ranges"
      clickable
      @table-item-clicked="onItemClick"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as Range)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import { filterActive, filterData, sortData } from '@/utils/table';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import { useToastStore } from '@/stores/toast';
import type { Range } from '@/types/Ranges';
import type { TableFilterData } from '@/types/Base';
import { getRanges as getRanges, removeRangesByIds } from '@/services/api/ranges';
import router from '@/router';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();

const ranges = ref<Range[]>([]);
const filterIsActive = ref<boolean | undefined>();

const rangesHeaders = [
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData();
});

async function loadData(data?: TableFilterData) {
  let response = await getRanges();
  if (!response) return;

  if (data && data.query) response = filterData(response, data.query, ['name', 'code']);
  if (data && data.column !== undefined && data.order)
    response = sortData(response, data.column, data.order, rangesHeaders);

  if (data && data.isActive !== undefined) response = filterActive(response, data.isActive);
  ranges.value = response;
}

function onAddNew() {
  router.push({ name: 'ranges-details', params: { id: uuidv4() } });
}

function onItemClick(item: Range) {
  router.push({ name: 'ranges-details', params: { id: item.id } });
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<Range>();
function onDeleteRow(item: Range) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  await onDeleteMultiselect([deleteRowItem.value]);
}

async function onDeleteMultiselect(items: Range[]) {
  const idsToDelete = items.filter((item) => item.isFromManager).map((item) => item.id);
  const res = await removeRangesByIds(idsToDelete);
  if (!res && ranges.value)
    ranges.value = ranges.value.filter((range) => !idsToDelete.includes(range.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
